import type { OutcomeShares } from "@screener/protocol";
import { SegmentBar, type SegmentBarSegment } from "@screener/uikit/components/segment-bar";
import { cn } from "@screener/uikit/lib/utils";
import { OUTCOME_SHARES } from "@/lib/labels";

/**
 * Доли исходов §9 (1.5) полосой. `SegmentBar` в UIKit не знает исходов:
 * какому исходу какой тон и штриховка — решение экрана (design.md
 * stage8-library-visual, решение 2). Недосмотренные `Lost` и `Stopped`
 * заштрихованы; цвет в одиночку ничего не сообщает — доли называет легенда.
 */

type SegmentLook = Pick<SegmentBarSegment, "tone" | "hatched">;

const OUTCOME_LOOKS: Record<keyof OutcomeShares, SegmentLook> = {
  held: { tone: "bid" },
  eaten: { tone: "ask" },
  pulled: { tone: "muted" },
  timeout: { tone: "subtle" },
  lost: { tone: "muted", hatched: true },
  stopped: { tone: "subtle", hatched: true },
};

/**
 * Отрезок одного исхода: тон и штриховка из общей таблицы. Им же рисуется
 * метка легенды, поэтому метка и отрезок полосы не расходятся.
 */
export function outcomeSegment(outcome: keyof OutcomeShares, value: number): SegmentBarSegment {
  return { key: outcome, value, ...OUTCOME_LOOKS[outcome] };
}

/**
 * Отрезки полосы в порядке `OUTCOME_SHARES`. Хотя бы одна доля `null` —
 * выборка пуста, демон долей не отдаёт: отрезков нет.
 */
export function outcomeSegments(shares: OutcomeShares): SegmentBarSegment[] | null {
  const segments: SegmentBarSegment[] = [];
  for (const outcome of OUTCOME_SHARES) {
    const value = shares[outcome];
    if (value === null) {
      return null;
    }
    segments.push(outcomeSegment(outcome, value));
  }
  return segments;
}

/**
 * Цвет текста доли: тот же, что у отрезка, только у содержательных исходов
 * (§116 п. 6). Цвет в одиночку ничего не сообщает — рядом имя исхода.
 */
const OUTCOME_TEXT: Partial<Record<keyof OutcomeShares, string>> = {
  held: "text-bid",
  eaten: "text-ask",
};

export function outcomeTextClass(outcome: keyof OutcomeShares): string | undefined {
  return OUTCOME_TEXT[outcome];
}

/** Метка цвета исхода для легенды: полоса из одного отрезка шириной `w-3`. */
export function OutcomeSwatch({
  outcome,
  className,
}: {
  outcome: keyof OutcomeShares;
  className?: string;
}) {
  return (
    <SegmentBar segments={[outcomeSegment(outcome, 1)]} className={cn("w-3 shrink-0", className)} />
  );
}

export interface OutcomeBarProps {
  shares: OutcomeShares;
  size?: "default" | "sm";
  className?: string;
}

/** Полоса исходов; при отсутствующих долях не рисуется. */
export function OutcomeBar({ shares, size, className }: OutcomeBarProps) {
  const segments = outcomeSegments(shares);
  if (segments === null) {
    return null;
  }
  return <SegmentBar segments={segments} size={size} className={className} />;
}
